import { PkButton } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { memo, useMemo } from 'react';

import type { EditorialContent } from '@cobalt/utilities';
import VideoPlayer from '@shared/VideoPlayer';
import { stripHtml } from '@utilities/dom';

import * as styles from './styles.module.scss';

const trackingData = {
  primaryBtn: {
    category: 'Click',
    action: 'Ticketing primary action',
    label: 'ticketing',
    name: 'ticketing',
    more: { section_reference: 'ticketing', interaction: 'Ticketing primary action' },
  },
  secondaryBtn: {
    category: 'Click',
    action: 'Ticketing secondary action',
    label: 'ticketing',
    name: 'ticketing',
    more: { section_reference: 'ticketing', interaction: 'Ticketing secondary action' },
  },
};

interface NormalHeaderProps {
  article: EditorialContent;
}

const NormalHeader = ({ article }: NormalHeaderProps) => {
  const [button, secondaryButton] = useMemo(
    () =>
      (article.document?.content || []).filter((elem) => elem._attributes?.['emk-type'].includes('button')).slice(0, 2),
    [article.document?.content],
  );
  const extraSummary = useMemo(
    () => article.body?.find((elem) => ['HEADING', 'PARAGRAPH'].includes(elem._type))?._html,
    [article.body],
  );

  return (
    <div className={clsx(styles.ticketingHeaderWrapInner, ' pk-container pk-d--flex pk-d-sm--grid')}>
      <div className='pk-col pk-col--span-12 pk-col--span-sm-6 pk-col--span-md-5 pk-col--span-lg-7  pk-d--flex pk-align-items--center'>
        <div className={styles.ticketingHeaderText}>
          <h1 className={clsx(styles.ticketingHeaderTitle, 'pk-text--text-01 pk-font--comp-bold pk-mb--s')}>
            {article.title}
          </h1>
          <p
            className={clsx(
              styles.ticketingHeaderDescription,
              'pk-text--text-01 pk-mb--l pk-font-size-lg--m pk-font-size-sm--s pk-font-size--m pk-line-height--lg',
            )}
          >
            {article.description}
          </p>
          <div className='pk-d--flex pk-flex--wrap pk-gap--s'>
            {secondaryButton?._text && (
              <PkButton
                className='js-tracking-link'
                appearance='outlined'
                highContrast
                linkHref={secondaryButton._attributes.href}
                linkTarget='_blank'
                data-tracking={JSON.stringify(trackingData.secondaryBtn)}
              >
                {secondaryButton._text}
              </PkButton>
            )}
            {button?._text && (
              <PkButton
                className='js-tracking-link'
                linkHref={button._attributes.href}
                linkTarget='_blank'
                data-tracking={JSON.stringify(trackingData.primaryBtn)}
              >
                {button._text}
              </PkButton>
            )}
          </div>
          {stripHtml(extraSummary) && (
            <div className={clsx(styles.ticketingHeaderExtra, 'pk-text--text-01 pk-font-size--xs pk-mt--l')}>
              {parseHtmlToReact(extraSummary ?? '')}
            </div>
          )}
        </div>
      </div>
      <div className={styles.ticketingHeaderImgDecoration} />
      {article?.mainMedia?.type === 'videostory' && (
        <VideoPlayer
          teaser={article?.mainMedia}
          wrapperClassName={styles.ticketingHeaderVideoWrapper}
          minimizeOnScroll={false}
          fullscreenOnClick
          repeat
          isAutoPlay
          isMuted
          unmuteOnFullscreen
          showTitle={false}
          showDescription={false}
        />
      )}
      {article.mainMedia?.type !== 'videostory' && (
        <figure className={styles.ticketingHeaderFigure}>
          <img className={styles.ticketingHeaderImg} alt='' src={article.imageUrl} />
        </figure>
      )}
    </div>
  );
};

export default memo(NormalHeader);
